import { ChangeEventHandler, FunctionComponent, InputHTMLAttributes, ReactNode } from 'react';
import { useController } from 'react-hook-form';
import { ErrorMessage } from 'softcat:components/error-message.tsx';
import { Form } from 'softcat:components/form.tsx';

type CheckboxOption = {
    disabled?: boolean;
    label: ReactNode;
    value: string;
};

type FormCheckboxProperties = InputHTMLAttributes<Omit<HTMLInputElement, 'type'>> & {
    label: ReactNode;
    name: string;
    options?: CheckboxOption[];
};

export const FormCheckbox: FunctionComponent<FormCheckboxProperties> = ({ label, name, options, ...fieldAttributes }) => {
    const { field, fieldState } = useController({ name });

    const onOptionsChange: ChangeEventHandler<HTMLInputElement> = (event) => {
        const checked = event.target.checked;
        const checkboxValue = event.target.value;

        const newValue = checked
            ? [...field.value, checkboxValue]
            : field.value.filter((value: string) => {
                  return value !== checkboxValue;
              });

        field.onChange(newValue);
    };

    return (
        <div className="sm:grid sm:grid-cols-4 sm:items-start sm:gap-4">
            <Form.Label htmlFor={name}>{label}</Form.Label>

            <div className={'mt-2 flex flex-col gap-2 sm:col-span-3 sm:mt-1.5'}>
                {options?.length ? (
                    options.map((option) => {
                        return (
                            <div className="flex items-center" key={option.value}>
                                <input
                                    {...field}
                                    {...fieldAttributes}
                                    checked={field.value?.includes(option.value)}
                                    className="h-4 w-4 rounded border-gray-200 text-indigo-600 shadow-sm focus:ring-indigo-600 disabled:opacity-50"
                                    disabled={option.disabled}
                                    id={`${name}-${option.value}`}
                                    onChange={onOptionsChange}
                                    type="checkbox"
                                    value={option.value}
                                />
                                <label className="ml-2" htmlFor={`${name}-${option.value}`}>
                                    {option.label}
                                </label>
                            </div>
                        );
                    })
                ) : (
                    <input
                        {...field}
                        {...fieldAttributes}
                        checked={field.value}
                        className="h-4 w-4 rounded border-gray-200 text-indigo-600 shadow-sm focus:ring-indigo-600 disabled:opacity-50"
                        id={name}
                        type="checkbox"
                    />
                )}

                {fieldState.error && (
                    <div className="mt-3">
                        <ErrorMessage>{fieldState.error.message}</ErrorMessage>
                    </div>
                )}
            </div>
        </div>
    );
};
